// features/inventory/pages/ABCClassificationPage.tsx
/**
 * ABC Classification Page — Product value/velocity analysis for cycle count scheduling.
 *
 * Route: /inventory/classification
 *
 * Features:
 * - ABC classification (A: high value, B: medium, C: low)
 * - Based on stock value (quantity × cost price)
 * - Visual distribution chart
 * - Per-class count frequency configuration
 * - Product list with classification badges
 * - Search and filter by class
 * - Generate cycle count for a specific class
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Pagination } from '@/components/ui/Pagination';
import {
    getStockLevels,
    getStockValuation,
    StockLevel,
} from '@/features/stock/services/stockApi';
import { createCountSession } from '../services/inventoryApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    ArrowLeft,
    Search,
    X,
    Package,
    Store,
    BarChart3,
    TrendingUp,
    Settings,
    Play,
    Layers,
    RefreshCw,
    Calculator,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// TYPES
// ============================================================

type ABCClass = 'A' | 'B' | 'C';

interface ClassifiedProduct {
    productId: string;
    productName: string;
    branchName: string;
    quantityOnHand: string;
    costPrice: string;
    totalValue: number;
    abcClass: ABCClass;
    countFrequency: string;
}

interface ClassConfig {
    label: string;
    color: string;
    bg: string;
    border: string;
    percentThreshold: number;
    countFrequency: string;
    countFrequencyDays: number;
}

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 30;

const CLASS_CONFIG: Record<ABCClass, ClassConfig> = {
    A: {
        label: 'A - High Value',
        color: 'text-rose-600',
        bg: 'bg-rose-50',
        border: 'border-rose-200',
        percentThreshold: 70,
        countFrequency: 'Weekly',
        countFrequencyDays: 7,
    },
    B: {
        label: 'B - Medium Value',
        color: 'text-amber-600',
        bg: 'bg-amber-50',
        border: 'border-amber-200',
        percentThreshold: 20,
        countFrequency: 'Monthly',
        countFrequencyDays: 30,
    },
    C: {
        label: 'C - Low Value',
        color: 'text-slate-600',
        bg: 'bg-slate-50',
        border: 'border-slate-200',
        percentThreshold: 10,
        countFrequency: 'Quarterly',
        countFrequencyDays: 90,
    },
};

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ABCClassificationPage() {
    const navigate = useNavigate();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // State
    const [branchId, setBranchId] = useState(selectedBranchId || '');
    const [search, setSearch] = useState('');
    const [classFilter, setClassFilter] = useState<ABCClass | 'all'>('all');
    const [page, setPage] = useState(1);
    const [frequencyConfig, setFrequencyConfig] = useState(CLASS_CONFIG);

    // Queries
    const { data: branches } = useQuery({
        queryKey: ['branches-abc', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const { data: valuation, isLoading, isError, refetch } = useQuery({
        queryKey: ['stock-valuation-abc', user?.tenantId, branchId],
        queryFn: () => getStockValuation(user!.tenantId, branchId || undefined),
        enabled: !!user?.tenantId,
    });

    // Mutations
    const generateCountMutation = useMutation({
        mutationFn: async (abcClass: ABCClass) => {
            const classProducts = classifiedProducts
                .filter((p) => p.abcClass === abcClass)
                .map((p) => p.productId);
            return createCountSession(user!.tenantId, {
                branchId,
                countType: 'cycle',
                productIds: classProducts,
                notes: `Auto-generated ${CLASS_CONFIG[abcClass].label} cycle count`,
            });
        },
        onSuccess: (count, variables) => {
            addToast({
                type: 'success',
                title: 'Cycle count created',
                message: `${CLASS_CONFIG[variables as ABCClass].label} count started with ${count.totalItems} products.`,
            });
            queryClient.invalidateQueries({ queryKey: ['inventory-counts'] });
            navigate(`/inventory/counts/${count.id}`);
        },
        onError: (err: any) => {
            addToast({
                type: 'error',
                title: 'Failed to create count',
                message: err?.message,
            });
        },
    });

    // Derived data
    const branchesList = (branches || []) as Array<{ id: string; name: string }>;
    const valuationData = (valuation || []) as any[];

    // Classify products
    const classifiedProducts: ClassifiedProduct[] = useMemo(() => {
        if (valuationData.length === 0) return [];

        // Sort by total value descending
        const sorted = [...valuationData].sort(
            (a, b) => parseFloat(b.totalValue || '0') - parseFloat(a.totalValue || '0')
        );

        const totalValue = sorted.reduce(
            (sum, item) => sum + parseFloat(item.totalValue || '0'),
            0
        );

        let cumulativePercent = 0;
        return sorted.map((item) => {
            const itemValue = parseFloat(item.totalValue || '0');
            const itemPercent = totalValue > 0 ? (itemValue / totalValue) * 100 : 0;
            cumulativePercent += itemPercent;

            let abcClass: ABCClass;
            if (cumulativePercent <= 70) abcClass = 'A';
            else if (cumulativePercent <= 90) abcClass = 'B';
            else abcClass = 'C';

            return {
                productId: item.productId,
                productName: item.productName || 'Unknown',
                branchName: item.branchName || 'Unknown',
                quantityOnHand: item.quantityOnHand || '0',
                costPrice: item.costPrice || '0',
                totalValue: itemValue,
                abcClass,
                countFrequency: frequencyConfig[abcClass].countFrequency,
            };
        });
    }, [valuationData, frequencyConfig]);

    // Filter and search
    const filteredProducts = useMemo(() => {
        let result = classifiedProducts;

        if (search) {
            const q = search.toLowerCase();
            result = result.filter((p) => p.productName.toLowerCase().includes(q));
        }

        if (classFilter !== 'all') {
            result = result.filter((p) => p.abcClass === classFilter);
        }

        return result;
    }, [classifiedProducts, search, classFilter]);

    // Pagination
    const totalCount = filteredProducts.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const paginatedProducts = filteredProducts.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    // Class stats
    const classStats = useMemo(() => {
        const aCount = classifiedProducts.filter((p) => p.abcClass === 'A').length;
        const bCount = classifiedProducts.filter((p) => p.abcClass === 'B').length;
        const cCount = classifiedProducts.filter((p) => p.abcClass === 'C').length;
        const aValue = classifiedProducts
            .filter((p) => p.abcClass === 'A')
            .reduce((sum, p) => sum + p.totalValue, 0);
        const bValue = classifiedProducts
            .filter((p) => p.abcClass === 'B')
            .reduce((sum, p) => sum + p.totalValue, 0);
        const cValue = classifiedProducts
            .filter((p) => p.abcClass === 'C')
            .reduce((sum, p) => sum + p.totalValue, 0);
        const totalValue = aValue + bValue + cValue;

        return {
            total: classifiedProducts.length,
            totalValue,
            aCount,
            aValue,
            aPercent: totalValue > 0 ? Math.round((aValue / totalValue) * 100) : 0,
            bCount,
            bValue,
            bPercent: totalValue > 0 ? Math.round((bValue / totalValue) * 100) : 0,
            cCount,
            cValue,
            cPercent: totalValue > 0 ? Math.round((cValue / totalValue) * 100) : 0,
        };
    }, [classifiedProducts]);

    const getBranchName = (id: string) =>
        branchesList.find((b) => b.id === id)?.name || 'Warehouse';

    const formatNaira = (val: number): string => {
        return `₦${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/inventory')}
                                className="p-2 rounded-xl hover:bg-slate-100 transition-colors"
                                type="button"
                            >
                                <ArrowLeft className="w-5 h-5 text-slate-600" />
                            </button>
                            <div className="p-2.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/25">
                                <BarChart3 className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    ABC Classification
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {getBranchName(branchId)} · {classStats.total} products ·{' '}
                                    {formatNaira(classStats.totalValue)} total value
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => refetch()}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                            type="button"
                        >
                            <RefreshCw className="w-4 h-4" /> Refresh
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-6">
                {/* Branch Selector */}
                {branchesList.length > 1 && (
                    <select
                        value={branchId}
                        onChange={(e) => {
                            setBranchId(e.target.value);
                            setPage(1);
                        }}
                        className="px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white"
                    >
                        {branchesList.map((b) => (
                            <option key={b.id} value={b.id}>
                                {b.name}
                            </option>
                        ))}
                    </select>
                )}

                {/* Class Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {(['A', 'B', 'C'] as ABCClass[]).map((cls) => {
                        const config = CLASS_CONFIG[cls];
                        const stats =
                            cls === 'A'
                                ? classStats.aCount
                                : cls === 'B'
                                    ? classStats.bCount
                                    : classStats.cCount;
                        const value =
                            cls === 'A'
                                ? classStats.aValue
                                : cls === 'B'
                                    ? classStats.bValue
                                    : classStats.cValue;
                        const percent =
                            cls === 'A'
                                ? classStats.aPercent
                                : cls === 'B'
                                    ? classStats.bPercent
                                    : classStats.cPercent;

                        return (
                            <Card
                                key={cls}
                                className={clsx('border-2', config.border)}
                            >
                                <CardContent className="p-5">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className={clsx('p-2.5 rounded-xl', config.bg)}>
                                            <Layers className={clsx('w-5 h-5', config.color)} />
                                        </div>
                                        <Badge variant="info" size="sm">
                                            {config.countFrequency}
                                        </Badge>
                                    </div>
                                    <h3 className={clsx('text-lg font-bold', config.color)}>
                                        Class {cls}
                                    </h3>
                                    <p className="text-xs text-slate-500 mt-1">{config.label}</p>
                                    <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                                        <div>
                                            <p className="text-[10px] text-slate-400 uppercase">Products</p>
                                            <p className="text-xl font-bold">{stats}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-slate-400 uppercase">Value</p>
                                            <p className="text-xl font-bold">
                                                {formatNaira(value)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mt-3">
                                        <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                                            <motion.div
                                                className={clsx('h-2 rounded-full', config.bg, config.color)}
                                                initial={{ width: 0 }}
                                                animate={{ width: `${percent}%` }}
                                                transition={{ duration: 1, ease: 'easeOut' }}
                                                style={{
                                                    background: cls === 'A'
                                                        ? 'linear-gradient(to right, #ef4444, #f97316)'
                                                        : cls === 'B'
                                                            ? 'linear-gradient(to right, #f59e0b, #eab308)'
                                                            : 'linear-gradient(to right, #64748b, #94a3b8)',
                                                }}
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-400 mt-1 text-right">
                                            {percent}% of total value
                                        </p>
                                    </div>
                                    <button
                                        onClick={() =>
                                            generateCountMutation.mutate(cls)
                                        }
                                        disabled={generateCountMutation.isPending || stats === 0}
                                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium hover:bg-slate-50 transition-all disabled:opacity-50"
                                        type="button"
                                    >
                                        <Play className="w-4 h-4" />
                                        Generate {config.label} Count
                                    </button>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>

                {/* Filters */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 text-sm"
                            />
                            {search && (
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setPage(1);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            {[
                                { value: 'all' as const, label: 'All Classes' },
                                { value: 'A' as const, label: 'Class A' },
                                { value: 'B' as const, label: 'Class B' },
                                { value: 'C' as const, label: 'Class C' },
                            ].map((f) => (
                                <button
                                    key={f.value}
                                    onClick={() => {
                                        setClassFilter(f.value);
                                        setPage(1);
                                    }}
                                    className={clsx(
                                        'px-3 py-2 rounded-lg text-xs font-medium transition-all',
                                        classFilter === f.value
                                            ? 'bg-white text-blue-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Products Table */}
                {isLoading ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : isError ? (
                    <ErrorState title="Failed to load valuation data" onRetry={() => refetch()} />
                ) : paginatedProducts.length === 0 ? (
                    <EmptyState
                        icon={<BarChart3 className="w-16 h-16" />}
                        title="No products found"
                        description="Upload stock data to see ABC classification."
                    />
                ) : (
                    <Card className="overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Product
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                            Branch
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Qty On Hand
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Cost Price
                                        </th>
                                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                            Total Value
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-24">
                                            Class
                                        </th>
                                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-32">
                                            Count Frequency
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {paginatedProducts.map((product, idx) => {
                                        const config = CLASS_CONFIG[product.abcClass];
                                        return (
                                            <motion.tr
                                                key={product.productId}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.02 }}
                                                className="hover:bg-slate-50 transition-colors"
                                            >
                                                <td className="px-4 py-3 text-sm font-medium text-slate-700">
                                                    {product.productName}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-slate-500">
                                                    {product.branchName}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm text-slate-700 tabular-nums">
                                                    {parseFloat(
                                                        product.quantityOnHand
                                                    ).toLocaleString()}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm text-slate-700 tabular-nums">
                                                    ₦{parseFloat(
                                                        product.costPrice
                                                    ).toLocaleString()}
                                                </td>
                                                <td className="px-4 py-3 text-right text-sm font-bold text-slate-900 tabular-nums">
                                                    {formatNaira(product.totalValue)}
                                                </td>
                                                <td className="px-4 py-3 text-center">
                                                    <Badge
                                                        variant={
                                                            product.abcClass === 'A'
                                                                ? 'danger'
                                                                : product.abcClass === 'B'
                                                                    ? 'warning'
                                                                    : 'neutral'
                                                        }
                                                        size="sm"
                                                    >
                                                        Class {product.abcClass}
                                                    </Badge>
                                                </td>
                                                <td className="px-4 py-3 text-center text-xs text-slate-500">
                                                    {product.countFrequency}
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalCount}
                                pageSize={PAGE_SIZE}
                                onPageChange={setPage}
                                onPageSizeChange={() => { }}
                            />
                        )}
                    </Card>
                )}
            </div>
        </div>
    );
}